// ============================================================
// sse.ts — Server-Sent Events parsing, shared by web and mobile.
//
// The client half of the streaming chat turn. A streamed response arrives as a
// byte stream of frames:
//
//     event: delta\n
//     data: {"text":"The cap "}\n
//     \n
//
// A frame ends at a BLANK LINE, and a chunk boundary lands wherever the network
// puts it — routinely mid-frame, sometimes mid-JSON. So the parser has to hold
// the partial tail between chunks rather than parsing each chunk on its own.
//
// This mirrors createSseParser() in api/src/conversations.js. The two are
// separate copies because api/ does not depend on @harvus/shared — keep their
// behaviour in step. Both are conformant readers of the same wire format
// (`event:` / `data:` / blank-line terminator), so there is little room for
// them to drift, and the frames they exchange are covered on both sides.
// ============================================================

export interface SseEvent {
  /** The frame's `event:` name, falling back to a `type` field on the payload. */
  event: string;
  /** The `data:` payload, JSON-parsed when it parses, otherwise the raw string. */
  data: any;
}

function parseSseBlock(block: string): SseEvent | null {
  let event: string | null = null;
  const dataLines: string[] = [];
  for (const rawLine of block.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.startsWith("event:")) event = line.slice(6).trim();
    // A leading space after the colon is part of the framing, not the payload.
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
  }
  if (!dataLines.length) return null;   // a comment or a bare keep-alive
  const raw = dataLines.join("\n");
  let data: any;
  try { data = JSON.parse(raw); } catch { data = raw; }
  return { event: event || (data && data.type) || "message", data };
}

/**
 * Incremental SSE reader. Feed it chunks in order; each call returns the frames
 * that completed within it. `flush()` drains a trailing frame that arrived
 * without its final blank line (a stream that closes cleanly right after its
 * last frame).
 */
// The end of a frame is a BLANK LINE, which the spec permits in either line
// ending — and a proxy in the middle is free to rewrite them. Searching for
// "\n\n" alone means a CRLF stream never yields a frame at all: the buffer just
// grows and the answer never appears. Scanned rather than normalised so a CRLF
// pair split across a chunk boundary still reunites in the buffer.
function frameEnd(buffer: string): { at: number; len: number } | null {
  const lf   = buffer.indexOf("\n\n");
  const crlf = buffer.indexOf("\r\n\r\n");
  if (lf === -1 && crlf === -1) return null;
  if (crlf !== -1 && (lf === -1 || crlf < lf)) return { at: crlf, len: 4 };
  return { at: lf, len: 2 };
}

export function createSseParser() {
  let buffer = "";
  return {
    push(chunk: string): SseEvent[] {
      buffer += chunk;
      const events: SseEvent[] = [];
      let end = frameEnd(buffer);
      while (end) {
        const block = buffer.slice(0, end.at);
        buffer = buffer.slice(end.at + end.len);
        const parsed = parseSseBlock(block);
        if (parsed) events.push(parsed);
        end = frameEnd(buffer);
      }
      return events;
    },
    flush(): SseEvent[] {
      const rest = buffer;
      buffer = "";
      const parsed = rest.trim() ? parseSseBlock(rest) : null;
      return parsed ? [parsed] : [];
    },
  };
}
